import React, { useCallback, useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";
import usePermissions from "@/lib/usePermissions";
import { CalendarDays, Plus, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";

const TYPE_STYLE = {
  company: "bg-purple-100 text-purple-700",
  optional: "bg-slate-100 text-slate-600",
  public: "bg-orange-100 text-orange-700",
};

const EMPTY_FORM = { date: "", name: "", type: "public", description: "" };

function weekdayLabel(iso) {
  try {
    return new Date(`${iso}T00:00:00`).toLocaleDateString(undefined, { weekday: "long" });
  } catch { return ""; }
}

export default function HolidayCalendarTab() {
  const can = usePermissions();
  const canCreate = can("leaves_create");
  const canEdit = can("leaves_edit");
  const canDelete = can("leaves_delete");
  const isAdmin = canCreate || canEdit || canDelete;
  const [year, setYear] = useState(2026);
  const [holidays, setHolidays] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/leaves/holidays", { params: { year } });
      setHolidays(data || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, [year]);

  useEffect(() => { load(); }, [load]);

  const sortedHolidays = useMemo(() => [...holidays].sort((a, b) => a.date.localeCompare(b.date)), [holidays]);

  const openAdd = () => { setEditing(null); setForm(EMPTY_FORM); setOpen(true); };
  const openEdit = (h) => { setEditing(h); setForm({ date: h.date, name: h.name, type: h.type || "public", description: h.description || "" }); setOpen(true); };

  const save = async () => {
    if (!form.date || !form.name.trim()) { toast.error("Date and name are required"); return; }
    setSaving(true);
    try {
      const payload = { date: form.date, name: form.name.trim(), type: form.type || "public", description: form.description.trim() || null };
      if (editing) {
        await api.put(`/leaves/holidays/${editing.id}`, payload);
        toast.success("Holiday updated");
      } else {
        await api.post("/leaves/holidays", payload);
        toast.success("Holiday added");
      }
      setOpen(false);
      setForm(EMPTY_FORM);
      setEditing(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const remove = async (h) => {
    if (!window.confirm(`Delete "${h.name}" (${h.date})? This cannot be undone.`)) return;
    try {
      await api.delete(`/leaves/holidays/${h.id}`);
      toast.success("Holiday removed");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="p-4 sm:p-8 max-w-7xl mx-auto" data-testid="holiday-calendar-tab">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div className="inline-flex items-center gap-2">
          <CalendarDays size={15} className="text-slate-500" />
          <span className="font-display font-semibold text-slate-800 text-sm">Company holiday calendar</span>
          <span className="text-xs text-slate-400">· excluded from working-day &amp; P&amp;L calculations</span>
        </div>
        <div className="inline-flex items-center gap-2">
          <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
            <SelectTrigger className="rounded-sm h-8 w-28" data-testid="holiday-year-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              {[2025, 2026, 2027].map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
            </SelectContent>
          </Select>
          {canCreate && (
            <Button onClick={openAdd} className="rounded-sm bg-slate-900 hover:bg-slate-800 text-white h-8" data-testid="add-holiday-btn">
              <Plus size={13} className="mr-1.5" />Add holiday
            </Button>
          )}
        </div>
      </div>

      <div className="card-flat overflow-hidden">
        <div className="overflow-x-auto">
          <div className="min-w-[720px]">
            <div className="grid grid-cols-12 gap-4 px-5 py-3 border-b border-slate-200 label-overline">
              <div className="col-span-2">Date</div>
              <div className="col-span-2">Day</div>
              <div className="col-span-3">Name</div>
              <div className="col-span-1">Type</div>
              <div className={isAdmin ? "col-span-3" : "col-span-4"}>Description</div>
              {isAdmin && <div className="col-span-1 text-right">Actions</div>}
            </div>
            <div className="divide-row">
              {loading && <div className="px-5 py-8 text-center text-sm text-slate-400">Loading…</div>}
              {!loading && sortedHolidays.length === 0 && (
                <div className="px-5 py-10 text-center text-sm text-slate-500" data-testid="holiday-calendar-empty">
                  No holidays added for {year} yet{isAdmin ? " — click \"Add holiday\" to create one." : "."}
                </div>
              )}
              {!loading && sortedHolidays.map((h) => (
                <div key={h.id} className="grid grid-cols-12 gap-4 px-5 py-3 items-center text-sm hover:bg-slate-50" data-testid={`holiday-calendar-row-${h.id}`}>
                  <div className="col-span-2 font-mono text-xs text-slate-600">{h.date}</div>
                  <div className="col-span-2 text-xs text-slate-500">{weekdayLabel(h.date)}</div>
                  <div className="col-span-3 text-slate-800 font-medium truncate" title={h.name}>{h.name}</div>
                  <div className="col-span-1">
                    <span className={`px-1.5 py-0.5 rounded-sm text-[10px] uppercase tracking-wider ${TYPE_STYLE[h.type] || TYPE_STYLE.public}`}>{h.type || "public"}</span>
                  </div>
                  <div className={`${isAdmin ? "col-span-3" : "col-span-4"} text-xs text-slate-500 truncate`} title={h.description || ""}>{h.description || "—"}</div>
                  {isAdmin && (
                    <div className="col-span-1 flex items-center justify-end gap-1">
                      {canEdit && <button onClick={() => openEdit(h)} className="p-1.5 text-slate-400 hover:text-slate-700" data-testid={`holiday-calendar-edit-${h.id}`}><Pencil size={13} /></button>}
                      {canDelete && <button onClick={() => remove(h)} className="p-1.5 text-slate-400 hover:text-red-600" data-testid={`holiday-calendar-delete-${h.id}`}><Trash2 size={13} /></button>}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setEditing(null); setForm(EMPTY_FORM); } }}>
        <DialogContent className="rounded-sm" data-testid="holiday-calendar-dialog">
          <DialogHeader><DialogTitle className="font-display">{editing ? "Edit holiday" : "Add holiday"}</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-2">
            <div>
              <Label className="label-overline">Date *</Label>
              <Input type="date" className="rounded-sm mt-1" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="holiday-calendar-date-input" />
            </div>
            <div>
              <Label className="label-overline">Name *</Label>
              <Input className="rounded-sm mt-1" placeholder="e.g. Republic Day" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="holiday-calendar-name-input" />
            </div>
            <div>
              <Label className="label-overline">Type</Label>
              <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                <SelectTrigger className="rounded-sm mt-1" data-testid="holiday-calendar-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="public">Public</SelectItem>
                  <SelectItem value="company">Company</SelectItem>
                  <SelectItem value="optional">Optional</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="label-overline">Description (optional)</Label>
              <Textarea className="rounded-sm mt-1" placeholder="e.g. Office closed" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="holiday-calendar-desc-input" />
            </div>
          </div>
          <DialogFooter>
            <Button onClick={save} disabled={saving} className="rounded-sm bg-slate-900 hover:bg-slate-800 text-white" data-testid="holiday-calendar-save-btn">
              {saving ? "Saving…" : editing ? "Save changes" : "Add holiday"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
